import { ArticleStatus, MediaPlacement, MediaType } from '@prisma/client';
import { z } from "zod";
import { mediaMetadataSchema } from './media-schema';

// Flexible media schema that accepts IMediaItem format
export const articleMediaSchema = z.object({
  id: z.string().optional(),
  url: z.string(),
  type: z.nativeEnum(MediaType),
  placement: z.nativeEnum(MediaPlacement).default(MediaPlacement.ARTICLE_MEDIA),
  mimeType: z.string(),
  size: z.number().min(0),
  width: z.number().nullable(),
  height: z.number().nullable(),
  altText: z.string().nullable(),
  caption: z.string().nullable(),
  aspectRatio: z.number().nullable(),
  dpi: z.number().nullable().optional(),
  metadata: z.any().optional(), // Flexible metadata to accept different formats
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
  status: z.enum(['pending', 'processing', 'ready', 'error', 'deleted']).optional(),
  version: z.number().optional(),
  processingComplete: z.boolean().optional(),
  isDeleted: z.boolean().optional()
}); // Remove passthrough to avoid index signature requirement

// Base schema for all article operations
export const baseArticleObject = z.object({
  title: z.string().min(1, "Title is required"),
  body: z.string(),
  excerpt: z.string().nullable().optional(),
  metaTitle: z.string().nullable().optional(),
  metaDescription: z.string().nullable().optional(),
  status: z.nativeEnum(ArticleStatus).optional().default(ArticleStatus.DRAFT),
  categoryId: z.string().nullable().transform((val) => val || ''),
  authorId: z.string().optional(),
  media: z.array(articleMediaSchema),
  tags: z.array(z.string()).optional().default(['#wraps']),
  keywords: z.array(z.string()).optional().default([]),
  publishedAt: z.string().nullable().optional(),
  viewCount: z.number().optional(),
  isDeleted: z.boolean().optional(),
  isFeatured: z.boolean().optional(),
  slug: z.string().optional(),
  statusChangeReason: z.string().nullable().optional(),
  statusHistory: z.array(z.object({
    status: z.nativeEnum(ArticleStatus),
    reason: z.string().nullable(),
    timestamp: z.string()
  })).optional().default([])
});

// Add refinements for non-draft validation
export const withRefinements = <T extends z.ZodTypeAny>(schema: T) => schema.superRefine((data: any, ctx) => {
  // Only apply validations for non-draft articles
  if (data.status !== ArticleStatus.DRAFT) {
    if (!data.body || data.body.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Content is required for non-draft articles",
        path: ["body"]
      });
    }

    if (!data.categoryId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Category is required for non-draft articles",
        path: ["categoryId"]
      });
    }

    if (!data.media || data.media.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one media item is required for non-draft articles",
        path: ["media"]
      });
    }
  }
});

export const articleBaseSchema = withRefinements(baseArticleObject);

// Schema for article creation
export const articleCreateSchema = articleBaseSchema;

// Schema for article updates
export const articleUpdateSchema = withRefinements(baseArticleObject.partial());

// Schema for client input
export const articleInputSchema = articleBaseSchema;

// Types
export type ArticleCreateInputDataSchema = z.output<typeof articleCreateSchema>;
export type ArticleUpdateInputDataSchema = z.output<typeof articleUpdateSchema>;
export type ArticleInputDataSchema = z.output<typeof articleInputSchema>;

// Define required fields for publishing
const REQUIRED_FIELDS_FOR_PUBLISHING = {
  DRAFT: [] as (keyof ArticleInputDataSchema)[],
  PUBLISHED: ['title', 'body', 'categoryId', 'media'] as (keyof ArticleInputDataSchema)[],
  DELETED: [] as (keyof ArticleInputDataSchema)[]
};

// Define status transition conditions
const STATUS_TRANSITIONS: Record<ArticleStatus, {
  allowedTransitions: ArticleStatus[];
  conditions: (data: ArticleInputDataSchema) => { valid: boolean; message?: string };
}> = {
  DRAFT: {
    allowedTransitions: [ArticleStatus.PUBLISHED, ArticleStatus.DELETED],
    conditions: (data: ArticleInputDataSchema) => {
      if (!data) return { valid: false, message: 'No data provided' };
      return { valid: true };
    }
  },
  PUBLISHED: {
    allowedTransitions: [ArticleStatus.DRAFT, ArticleStatus.DELETED],
    conditions: (data: ArticleInputDataSchema) => {
      if (!data) return { valid: false, message: 'No data provided' };
      return { valid: true };
    }
  },
  DELETED: {
    allowedTransitions: [ArticleStatus.DRAFT],
    conditions: (data: ArticleInputDataSchema) => {
      if (!data) return { valid: false, message: 'No data provided' };
      return { valid: true };
    }
  }
};

// Export helper functions
export function getValidStatusTransitions(currentStatus: ArticleStatus): ArticleStatus[] {
  return STATUS_TRANSITIONS[currentStatus]?.allowedTransitions || [];
}

export function validateStatusTransition(
  currentStatus: ArticleStatus,
  newStatus: ArticleStatus,
  data: ArticleInputDataSchema
): { valid: boolean; message?: string } {
  // Check if transition is allowed
  if (!STATUS_TRANSITIONS[currentStatus]?.allowedTransitions.includes(newStatus)) {
    return {
      valid: false,
      message: `Cannot transition from ${currentStatus} to ${newStatus}`
    };
  }

  // Check if required fields are present for the new status
  const requiredFields = REQUIRED_FIELDS_FOR_PUBLISHING[newStatus];
  const missingFields = requiredFields.filter(field => !data[field]);

  if (missingFields.length > 0) {
    return {
      valid: false,
      message: `Missing required fields for ${newStatus}: ${missingFields.join(', ')}`
    };
  }

  // Check additional conditions
  return STATUS_TRANSITIONS[currentStatus].conditions(data);
}

// Helper to check if an article can be published
export function canPublishArticle(data: Partial<ArticleInputDataSchema>): {
  valid: boolean;
  message?: string;
} {
  // Convert partial data to full data with defaults
  const fullData: ArticleInputDataSchema = {
    title: data.title || '',
    body: data.body || '',
    excerpt: data.excerpt || null,
    metaTitle: data.metaTitle || null,
    metaDescription: data.metaDescription || null,
    status: data.status || 'DRAFT',
    keywords: data.keywords || [],
    tags: data.tags || [],
    categoryId: data.categoryId || '',
    media: data.media || [],
    statusChangeReason: data.statusChangeReason || null,
    statusHistory: data.statusHistory || [],
    slug: data.slug || '',
    publishedAt: data.publishedAt || null,
    viewCount: data.viewCount || 0,
    isDeleted: data.isDeleted || false,
    isFeatured: data.isFeatured || false,
  };

  return STATUS_TRANSITIONS['DRAFT'].conditions(fullData);
}

// Schema for filters
export const articleFiltersSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISHED', 'DELETED']).optional(),
  categoryId: z.string().optional(),
  tags: z.array(z.string()).optional(),
  search: z.string().optional(),
  page: z.number().min(1, "Page must be at least 1"),
  take: z.number().min(1, "Take must be at least 1"),
  orderBy: z.enum(['createdAt', 'updatedAt', 'title']).optional(),
  order: z.enum(['asc', 'desc']).optional()
});

export type ArticleFiltersInputDataSchema = z.output<typeof articleFiltersSchema>;

// Define the form schema using Zod
export const articleFormSchema = withRefinements(z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title is too long'),
  body: z.string(),
  excerpt: z.string().max(200, 'Excerpt must be less than 200 characters').nullable(),
  metaTitle: z.string().nullable().optional(),
  metaDescription: z.string().nullable().optional(),
  categoryId: z.string(),
  authorId: z.string().optional(),
  status: z.nativeEnum(ArticleStatus).default(ArticleStatus.DRAFT),
  isFeatured: z.boolean().default(false),
  media: z.array(articleMediaSchema),
  tags: z.array(z.string()).default([]),
  keywords: z.array(z.string()).default([]),
  publishedAt: z.string().nullable().optional(),
  viewCount: z.number().optional(),
  isDeleted: z.boolean().optional(),
  slug: z.string().optional(),
  statusHistory: z.array(z.any()).default([]),
  statusChangeReason: z.string().nullable().optional()
}));

export type ArticleFormData = z.output<typeof articleFormSchema>
